import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useRegion } from "context/RegionContext";
import { useHeaderActionValue } from "context/HeaderActionContext";
import { callNotiUnreadCount } from "api/MuyeonApi";
import usePermission from "hooks/usePermission";
import RoleNoticeModal from "components/ui/RoleNoticeModal";
import { openNativeOrWeb } from "common/nativeRoute";
import { openNotifications } from "common/nativeBridge";

// 무용연 상단바.
// isMain 의 위치 버튼 클릭 → 관심 지역 관리 페이지(이용식당 패턴)로 이동.
// action = { icon, to, gate } → 우측 아이콘 버튼(예: 커뮤니티 글쓰기). gate 있으면 권한 게이팅.
const Header = ({ title, showBack, isMain, isMyPage, mainColor, onBack, action }) => {
  const navigate = useNavigate();
  const { active } = useRegion();
  const { gate, notice, closeNotice } = usePermission();
  // 페이지가 주입한 상단바 우측 액션(옵션, 예: 임시저장). 배열이면 순서대로 나란히(예: [임시저장, 미리보기]).
  const injected = useHeaderActionValue();
  const rightActions = (Array.isArray(injected) ? injected : [injected]).filter(Boolean);
  const [noti, setNoti] = useState(0); // 안읽음 알림 수(홈 벨 뱃지)

  const onAction = () => {
    if (!action?.to) return;
    const go = () => navigate(action.to);
    if (action.gate) gate(action.gate, go);
    else go();
  };

  // 홈에서만 알림 안읽음 수 조회(벨 뱃지). 진입 시 + 재포커스 시 갱신.
  useEffect(() => {
    if (!isMain) return;
    let alive = true;
    const fetchCount = () => callNotiUnreadCount()
      .then((r) => {
        // 백엔드가 숫자(raw) 또는 {count} 형태 모두 대응
        const c = Number(r?.data?.count ?? r?.data ?? 0);
        if (alive) setNoti(Number.isFinite(c) ? c : 0);
      })
      .catch(() => {});
    fetchCount();
    const onVis = () => { if (document.visibilityState === "visible") fetchCount(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVis); };
  }, [isMain]);

  return (
    <div className={`top-bar ${isMain || isMyPage || mainColor ? "bg-primary" : ""}`}>
      {showBack ? (
        <button type="button" className="ico-btn back" onClick={onBack}>
          <i className="arrow" />
          <span className="visually-hidden">뒤로가기</span>
        </button>
      ) : (
        <span />
      )}

      {isMain ? (
        <div className="main">
          <button
            type="button"
            className="page-name"
            onClick={() => navigate("/myRegions")}
          >
            <i className="location" />
            {active?.name || "지역 선택"}
            <i className="arrow-r-b" />
          </button>

          <div className="quick-btn-group">
            <button
              type="button"
              className="quick-btn"
              onClick={() => {
                // 앱은 네이티브 알림함(MyHome 벨과 동일), 웹은 매핑 테이블의 /notifications
                openNativeOrWeb(openNotifications, {}, navigate);
              }}
              style={{ position: "relative" }}
            >
              <i className="bell" />
              {noti > 0 && (
                <span style={{
                  position: "absolute", top: "0.2rem", right: "0.2rem",
                  minWidth: "1.6rem", height: "1.6rem", padding: "0 0.4rem",
                  background: "#e32502", color: "#fff", borderRadius: "99rem",
                  fontSize: "1rem", fontWeight: 700, lineHeight: 1,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}>{noti > 99 ? "99+" : noti}</span>
              )}
              <span className="visually-hidden">알림</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <h2 className="page-name" style={{ margin: 0 }}>
            {title}
          </h2>
          {rightActions.map((rightAction, i) => (
            rightAction.icon || rightAction.node ? (
              <button
                key={rightAction.label || i}
                type="button"
                className="ico-btn"
                style={i === 0 ? { marginLeft: "auto" } : undefined}
                onClick={rightAction.onClick}
                disabled={rightAction.disabled}
              >
                {/* top-bar .ico-btn i 전역 rotate(90deg)는 뒤로가기 화살표용 — 우측 액션 아이콘(?, + 등)은 원방향 유지 */}
                {/* node = 아이콘 파일이 없는 경우의 인라인 SVG(예: 공고 공유). 없으면 기존 아이콘 클래스. */}
                {rightAction.node || <i className={rightAction.icon} style={{ transform: "none" }} />}
                <span className="visually-hidden">{rightAction.label || ""}</span>
              </button>
            ) : (
              <button
                key={rightAction.label || i}
                type="button"
                className="action-btn"
                onClick={rightAction.onClick}
                disabled={rightAction.disabled}
              >
                {rightAction.label}
              </button>
            )
          ))}
        </>
      )}

      {/* 우측 액션 아이콘(예: 커뮤니티 글쓰기). 좌측 정렬 flex → margin-left:auto 로 우측 고정. */}
      {action?.icon && (
        <button
          type="button"
          className="ico-btn"
          style={{ marginLeft: "auto" }}
          onClick={onAction}
        >
          <i className={action.icon} />
          <span className="visually-hidden">{action.label || "쓰기"}</span>
        </button>
      )}

      <RoleNoticeModal notice={notice} onClose={closeNotice} />
    </div>
  );
};

export default Header;
